import { MediaitemModel } from '@models';
import { MediaitemService } from '@services';
import { setupForDark } from '@theme';
import { useNetworkState } from '@uidotdev/usehooks';
import classNames from 'classnames';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import MediaitemAccessError, { MediaitemErrorCode } from './MediaitemAccessError';

const MediaitemVideoThumbnail = ({
  mediaitem,
  type = 'small'
}: {
  mediaitem: MediaitemModel;
  type: 'small' | 'large';
}) => {
  const [thumbnailSrc, setThumbnailSrc] = useState<string | null>(null);
  const [accessError, setAccessError] = useState({
    hasAccessError: false,
    errorCode: undefined
  });
  const mediaitemService = new MediaitemService(mediaitem);
  const network = useNetworkState();

  useEffect(() => {
    setAccessError({
      hasAccessError: false,
      errorCode: undefined
    });
  }, [mediaitem]);

  useEffect(() => {
    if (!network.online) {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_NETWORK
      });
    } else if (
      accessError.hasAccessError &&
      accessError.errorCode === MediaitemErrorCode.MEDIA_ERR_NETWORK
    ) {
      setAccessError({
        hasAccessError: false,
        errorCode: undefined
      });
    }

    const retryFetch = async (attempts = 5, delay = 1000) => {
      for (let i = 0; i < attempts; i++) {
        const src = mediaitemService.getThumbnail();
        if (src) {
          setThumbnailSrc(src);
          return;
        }
        if (i < attempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, i * delay));
        }
      }
    };

    retryFetch();
  }, [mediaitem, network.online]);

  if (accessError.hasAccessError) {
    return (
      <MediaitemAccessError
        type='video'
        errorCode={accessError.errorCode}
        className={type === 'small' ? 'w-[150px] h-[150px]' : 'w-full aspect-square'}
      />
    );
  }

  return (
    <div className={classNames('relative overflow-hidden', type === 'small' ? 'w-fit' : 'w-full')}>
      {thumbnailSrc ? (
        <Image
          alt='video thumbnail'
          src={thumbnailSrc}
          width={type === 'small' ? 150 : 500}
          height={type === 'small' ? 150 : 500}
          className={classNames(
            'border-[1px]',
            'relative',
            'rounded-xs',
            type === 'small' ? 'w-[150px] h-[150px]' : 'w-full aspect-square object-cover'
          )}
        />
      ) : (
        <div
          className={classNames(
            'relative',
            'rounded-xs',
            setupForDark('bg-secondary'),
            type === 'small' ? 'w-[150px] h-[150px]' : 'w-full aspect-square object-cover'
          )}
        ></div>
      )}
    </div>
  );
};

export default MediaitemVideoThumbnail;
